import React, { useState, useEffect } from 'react';
import { Plus, Building2, Users, Settings, Search, Filter, Edit2, Trash2, Eye, UserPlus, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { useToast } from '../ui/ToastContext';
import useSupabase from '../../hooks/useSupabase';
import ViewLayout from '../ui/ViewLayout';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormGroup from '../ui/FormGroup';
import OrganizationProfileView from './OrganizationProfileView';

const OrganizationManagementView = ({ currentUser }) => {
  const { toast } = useToast();
  const { 
    fetchOrganizations, 
    fetchUsers, 
    saveOrganization, 
    deleteOrganization,
    // User management functions
    fetchOrganizationUsers,
    addUserToOrganization,
    updateUser,
    removeUserFromOrganization,
    transferUserToOrganization
  } = useSupabase();
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showProfileView, setShowProfileView] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [editingFromProfile, setEditingFromProfile] = useState(false);

  // User management state
  const [orgUsers, setOrgUsers] = useState([]);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({
    full_name: '',
    role: 'general',
    location: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    org_type: 'community_theater',
    tier: 'basic',
    status: 'active',
    max_users: 10,
    notes: '',
    contact_info: {
      email: '',
      phone: '',
      address: ''
    },
    pickup_days: ''
  });

  const orgTypes = [
    { value: 'community_theater', label: 'Community Theater' },
    { value: 'professional_theater', label: 'Professional Theater' },
    { value: 'school', label: 'School' },
    { value: 'corporate', label: 'Corporate' }
  ];

  const tiers = [
    { value: 'basic', label: 'Basic' },
    { value: 'premium', label: 'Premium' },
    { value: 'enterprise', label: 'Enterprise' }
  ];

  const statuses = [
    { value: 'active', label: 'Active', color: 'green' },
    { value: 'inactive', label: 'Inactive', color: 'red' },
    { value: 'pending', label: 'Pending', color: 'yellow' }
  ];

  // Load organizations and users
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [orgsData, usersData] = await Promise.all([
        fetchOrganizations(),
        fetchUsers()
      ]);
      setOrganizations(orgsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter organizations
  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (org.location || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || org.status === statusFilter;
    const matchesType = typeFilter === 'all' || org.org_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await saveOrganization(formData, selectedOrganization);
      toast.success(selectedOrganization ? 'Organization updated!' : 'Organization created!');
      
      setIsCreateModalOpen(false);
      setIsEditModalOpen(false);
      
      // Return to profile view if we were editing from there
      if (editingFromProfile) {
        setEditingFromProfile(false);
        setShowProfileView(true);
      } else {
        resetForm();
      }
      
      loadData();
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error(`Failed to save organization: ${error.message}`);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedOrganization) return;

    try {
      await deleteOrganization(selectedOrganization.id);
      toast.success('Organization deleted!');
      setIsDeleteModalOpen(false);
      setSelectedOrganization(null);
      loadData();
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast.error('Failed to delete organization');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      org_type: 'community_theater',
      tier: 'basic',
      status: 'active',
      max_users: 10,
      notes: '',
      contact_info: {
        email: '',
        phone: '',
        address: ''
      },
      pickup_days: ''
    });
    setSelectedOrganization(null);
  };

  // Open edit modal
  const openEditModal = (org) => {
    setSelectedOrganization(org);
    setFormData({
      name: org.name || '',
      location: org.location || '',
      org_type: org.org_type || 'community_theater',
      tier: org.tier || 'basic',
      status: org.status || 'active',
      max_users: org.max_users || 10,
      notes: org.notes || '',
      contact_info: org.contact_info ? JSON.parse(org.contact_info) : {
        email: '',
        phone: '',
        address: ''
      },
      pickup_days: org.pickup_days || ''
    });
    setIsEditModalOpen(true);
  };

  // Handle profile view navigation
  const openProfileView = (orgId) => {
    setSelectedOrgId(orgId);
    setShowProfileView(true);
  };

  const closeProfileView = () => {
    setShowProfileView(false);
    setSelectedOrgId(null);
  };

  const handleEditFromProfile = (org) => {
    // Close profile view and open edit modal
    setShowProfileView(false);
    setEditingFromProfile(true);
    openEditModal(org);
  };

  // ============ USER MANAGEMENT FUNCTIONS ============

  // Load users for the selected organization
  const loadOrgUsers = async (organizationId) => {
    try {
      const users = await fetchOrganizationUsers(organizationId);
      setOrgUsers(users);
    } catch (error) {
      console.error('Error loading organization users:', error);
      toast.error('Failed to load users');
    }
  };

  // Open user management modal
  const openUserModal = async (org) => {
    setSelectedOrganization(org);
    await loadOrgUsers(org.id);
    setIsUserModalOpen(true);
  };

  // Reset user form
  const resetUserForm = () => {
    setUserForm({
      full_name: '',
      role: 'general',
      location: ''
    });
    setSelectedUser(null);
    setIsEditingUser(false);
  };

  // Handle user form submission
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditingUser && selectedUser) {
        // Update existing user
        await updateUser(selectedUser.id, userForm);
        toast.success('User updated successfully!');
      } else {
        // Add new user
        await addUserToOrganization(userForm, selectedOrganization.id);
        toast.success('User added successfully!');
      }
      
      resetUserForm();
      await loadOrgUsers(selectedOrganization.id);
      loadData(); // Refresh main data
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(`Failed to ${isEditingUser ? 'update' : 'add'} user: ${error.message}`);
    }
  };

  // Edit user
  const editUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      full_name: user.full_name || '',
      role: user.role || 'general',
      location: user.location || ''
    });
    setIsEditingUser(true);
  };

  // Remove user from organization
  const handleRemoveUser = async (user) => {
    if (!window.confirm(`Are you sure you want to remove ${user.full_name} from this organization?`)) {
      return;
    }

    try {
      await removeUserFromOrganization(user.id);
      toast.success('User removed from organization!');
      await loadOrgUsers(selectedOrganization.id);
      loadData(); // Refresh main data
    } catch (error) {
      console.error('Error removing user:', error);
      toast.error('Failed to remove user');
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'inactive': return <AlertCircle className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const stats = [
    <ViewLayout.Stat 
      title="Total Organizations" 
      value={organizations.length} 
      icon={Building2} 
      color="blue" 
    />,
    <ViewLayout.Stat 
      title="Active Organizations" 
      value={organizations.filter(org => org.status === 'active').length} 
      icon={CheckCircle} 
      color="green" 
    />,
    <ViewLayout.Stat 
      title="Total Users" 
      value={users.length} 
      icon={Users} 
      color="purple" 
    />,
    <ViewLayout.Stat 
      title="Pending Approvals" 
      value={organizations.filter(org => org.status === 'pending').length} 
      icon={Clock} 
      color="yellow" 
    />
  ];

  const actions = [
    <Button
      onClick={() => {
        resetForm();
        setIsCreateModalOpen(true);
      }}
      className="bg-green-600 hover:bg-green-700"
    >
      <Plus className="h-4 w-4 mr-2" />
      New Organization
    </Button>
  ];

  // Show profile view if selected
  if (showProfileView && selectedOrgId) {
    return (
      <OrganizationProfileView
        organizationId={selectedOrgId}
        onBack={closeProfileView}
        onEditOrganization={handleEditFromProfile}
      />
    );
  }

  return (
    <ViewLayout
      title="Organization Management"
      subtitle="Manage warehouse organizations and user assignments"
      stats={stats}
      actions={actions}
    >
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            {statuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            {orgTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>

          {/* Clear Filters */}
          <Button
            variant="outline"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setTypeFilter('all');
            }}
          >
            <Filter className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Loading organizations...
                  </td>
                </tr>
              ) : filteredOrganizations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    No organizations found
                  </td>
                </tr>
              ) : (
                filteredOrganizations.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <button 
                          onClick={() => openProfileView(org.id)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline cursor-pointer"
                        >
                          {org.name}
                        </button>
                        <div className="text-sm text-gray-500">{org.location}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className="text-sm text-gray-900">
                          {orgTypes.find(t => t.value === org.org_type)?.label}
                        </span>
                        <div className="text-xs text-gray-500">
                          {tiers.find(t => t.value === org.tier)?.label} tier
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        org.status === 'active' ? 'bg-green-100 text-green-800' :
                        org.status === 'inactive' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {getStatusIcon(org.status)}
                        <span className="ml-1">{statuses.find(s => s.value === org.status)?.label}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {org.current_users} / {org.max_users}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(org.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openUserModal(org)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Manage Users"
                        >
                          <Users className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(org)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedOrganization(org);
                            setIsDeleteModalOpen(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Organization Modal */}
      <Modal
        isOpen={isCreateModalOpen || isEditModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          
          if (editingFromProfile) {
            // Return to profile view if we were editing from there
            setEditingFromProfile(false);
            setShowProfileView(true);
          } else {
            resetForm();
          }
        }}
        title={selectedOrganization ? 'Edit Organization' : 'Create Organization'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Organization Name" required>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </FormGroup>

            <FormGroup label="Location">
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </FormGroup>

            <FormGroup label="Organization Type" required>
              <select
                value={formData.org_type}
                onChange={(e) => setFormData({...formData, org_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {orgTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </FormGroup>

            <FormGroup label="Tier" required>
              <select
                value={formData.tier}
                onChange={(e) => setFormData({...formData, tier: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {tiers.map(tier => (
                  <option key={tier.value} value={tier.value}>{tier.label}</option>
                ))}
              </select>
            </FormGroup>

            <FormGroup label="Status" required>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </FormGroup>

            <FormGroup label="Max Users">
              <input
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData({...formData, max_users: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                min="1"
                max="1000"
              />
            </FormGroup>
          </div>

          {/* Contact Information */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormGroup label="Email">
                <input
                  type="email"
                  value={formData.contact_info.email}
                  onChange={(e) => setFormData({
                    ...formData,
                    contact_info: {...formData.contact_info, email: e.target.value}
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </FormGroup>

              <FormGroup label="Phone">
                <input
                  type="tel"
                  value={formData.contact_info.phone}
                  onChange={(e) => setFormData({
                    ...formData,
                    contact_info: {...formData.contact_info, phone: e.target.value}
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </FormGroup>
            </div>

            <FormGroup label="Address">
              <textarea
                value={formData.contact_info.address}
                onChange={(e) => setFormData({
                  ...formData,
                  contact_info: {...formData.contact_info, address: e.target.value}
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="2"
              />
            </FormGroup>

            <FormGroup label="Pickup Days">
              <input
                type="text"
                value={formData.pickup_days}
                onChange={(e) => setFormData({...formData, pickup_days: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Monday, Wednesday, Friday"
              />
            </FormGroup>

            <FormGroup label="Notes">
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="3"
              />
            </FormGroup>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCreateModalOpen(false);
                setIsEditModalOpen(false);
                
                if (editingFromProfile) {
                  // Return to profile view if we were editing from there
                  setEditingFromProfile(false);
                  setShowProfileView(true);
                } else {
                  resetForm();
                }
              }}
            >
              Cancel
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              {selectedOrganization ? 'Update Organization' : 'Create Organization'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedOrganization(null);
        }}
        title="Delete Organization"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{selectedOrganization?.name}</strong>? 
            This action cannot be undone and will remove all associated data.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedOrganization(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Organization
            </Button>
          </div>
        </div>
      </Modal>

      {/* User Management Modal */}
      <Modal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setSelectedOrganization(null);
          resetUserForm();
        }}
        title={`Manage Users - ${selectedOrganization?.name}`}
        size="large"
      >
        <div className="space-y-6">
          {/* Add/Edit User Form */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">
              {isEditingUser ? 'Edit User' : 'Add New User'}
            </h4>
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormGroup label="Full Name" required>
                  <input
                    type="text"
                    value={userForm.full_name}
                    onChange={(e) => setUserForm({...userForm, full_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </FormGroup>

                <FormGroup label="Role" required>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="general">General User</option>
                    <option value="warehouse_admin">Warehouse Admin</option>
                    <option value="service_provider">Service Provider</option>
                  </select>
                </FormGroup>

                <FormGroup label="Location">
                  <input
                    type="text"
                    value={userForm.location}
                    onChange={(e) => setUserForm({...userForm, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="City, State"
                  />
                </FormGroup>
              </div>
              
              <div className="flex justify-end gap-3">
                {isEditingUser && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetUserForm}
                  >
                    Cancel Edit
                  </Button>
                )}
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <UserPlus className="h-4 w-4 mr-2" />
                  {isEditingUser ? 'Update User' : 'Add User'}
                </Button>
              </div>
            </form>
          </div>

          {/* Current Users List */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Current Users ({orgUsers.length})
            </h4>
            {orgUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No users assigned to this organization</p>
                <p className="text-sm">Add users using the form above.</p>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orgUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-900">{user.full_name || 'Unnamed User'}</div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              user.role === 'warehouse_admin' ? 'bg-purple-100 text-purple-800' :
                              user.role === 'service_provider' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {user.role === 'warehouse_admin' ? 'Admin' :
                               user.role === 'service_provider' ? 'Service' : 'General'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {user.location || 'Not specified'}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-4 text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => editUser(user)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit User"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleRemoveUser(user)}
                                className="text-red-600 hover:text-red-900"
                                title="Remove User"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Modal Actions */}
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={() => {
                setIsUserModalOpen(false);
                setSelectedOrganization(null);
                resetUserForm();
              }}
              variant="outline"
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </ViewLayout>
  );
};

export default OrganizationManagementView;
